import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase/firebase-config";
import { useAuth } from "@/firebase/useAuth";
import { slugify } from "@/services/customLesson";

export type PendingGeneration = {
  lessonId: string;
  domainId: string;
  lessonTitle: string;
};

type LessonGenerationContextType = {
  /** Call this as soon as lesson generation starts so the global context can track it. */
  trackGeneration: (lessonId: string, domainId: string, lessonTitle: string) => void;
  /** Set when generation finishes. Consumed by the toast. */
  readyLesson: PendingGeneration | null;
  /** The lessonId currently being tracked (if any). */
  pendingLessonId: string | null;
  /** Dismiss only if the given lessonId is the one currently tracked. Safe to call from
   *  stale closures — the check runs inside the context where state is always fresh. */
  dismissIfTracked: (lessonId: string) => void;
  /** Unconditionally dismiss the toast and clear all pending state. */
  dismiss: () => void;
};

const LessonGenerationContext = createContext<LessonGenerationContextType>({
  trackGeneration: () => {},
  readyLesson: null,
  pendingLessonId: null,
  dismissIfTracked: () => {},
  dismiss: () => {},
});

const STORAGE_KEY = "lessonGeneration_pending";

function savePending(p: PendingGeneration | null) {
  try {
    if (p) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (_) {}
}

function loadPending(): PendingGeneration | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingGeneration) : null;
  } catch (_) {
    return null;
  }
}

export function LessonGenerationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // Restore any in-progress generation that was saved before a page reload
  const [pendingGeneration, setPendingGeneration] = useState<PendingGeneration | null>(
    () => loadPending()
  );
  const [readyLesson, setReadyLesson] = useState<PendingGeneration | null>(null);

  // Always-current ref — the snapshot closure reads this without needing to re-subscribe
  const pendingRef = useRef<PendingGeneration | null>(pendingGeneration);
  pendingRef.current = pendingGeneration;

  // Primitive string dep for the Firestore effect — only re-subscribes when the actual
  // lesson being tracked changes, not on every new object reference from trackGeneration.
  const [watchedLessonId, setWatchedLessonId] = useState<string | null>(
    () => loadPending()?.lessonId ?? null
  );

  const trackGeneration = useCallback(
    (lessonId: string, domainId: string, lessonTitle: string) => {
      const next = { lessonId, domainId, lessonTitle };
      pendingRef.current = next;
      savePending(next);
      setPendingGeneration(next);
      setReadyLesson(null);
      setWatchedLessonId(lessonId);
    },
    []
  );

  const dismiss = useCallback(() => {
    pendingRef.current = null;
    savePending(null);
    setReadyLesson(null);
    setPendingGeneration(null);
    setWatchedLessonId(null);
  }, []);

  // Safe to call from stale closures — always reads fresh pendingRef
  const dismissIfTracked = useCallback((lessonId: string) => {
    if (pendingRef.current?.lessonId === lessonId) {
      pendingRef.current = null;
      savePending(null);
      setReadyLesson(null);
      setPendingGeneration(null);
      setWatchedLessonId(null);
    }
  }, []);

  // Watch the lesson Firestore doc. Depends on watchedLessonId (primitive string) so the
  // subscription is stable — it only re-subscribes when the tracked lesson actually changes.
  useEffect(() => {
    if (!user || !watchedLessonId) return;

    const lessonDocRef = doc(
      db(),
      "users",
      user.uid,
      "geminiLessons",
      slugify(watchedLessonId)
    );

    // Track whether the doc has ever existed within this subscription.
    // The first Firestore snapshot may fire from local cache as "doesn't exist" simply
    // because the server hasn't written the sentinel yet — this is NOT a failure.
    // We only treat !snap.exists() as a failure after the doc has been seen to exist.
    let docHasExisted = false;

    const unsubscribe = onSnapshot(
      lessonDocRef,
      (snap) => {
        if (!snap.exists()) {
          if (docHasExisted) {
            // Doc existed then disappeared — server failed and deleted the sentinel.
            // LessonPage will surface the error. Just clean up tracking here.
            if (pendingRef.current) {
              pendingRef.current = null;
              savePending(null);
              setPendingGeneration(null);
              setWatchedLessonId(null);
              setReadyLesson(null);
            }
          }
          // else: doc doesn't exist yet — server hasn't written the sentinel. Just wait.
          return;
        }

        docHasExisted = true;
        const data = snap.data();

        // Only fire the toast when the generation lock is released AND
        // lesson content is present — never on the in-progress sentinel.
        const isComplete = data.generating === false && Boolean(data.lessonScript);
        if (isComplete && pendingRef.current) {
          setReadyLesson(pendingRef.current);
        }
      },
      (err) => {
        console.warn("[LessonGenerationContext] Firestore listener error:", err);
      }
    );

    return () => unsubscribe();
  }, [user, watchedLessonId]);

  return (
    <LessonGenerationContext.Provider
      value={{
        trackGeneration,
        readyLesson,
        pendingLessonId: pendingGeneration?.lessonId ?? null,
        dismissIfTracked,
        dismiss,
      }}
    >
      {children}
    </LessonGenerationContext.Provider>
  );
}

export function useLessonGeneration() {
  return useContext(LessonGenerationContext);
}
